import type { UpdateContactTrainingInfoRequestDto } from '@cobliteam/common-types';
import gql from 'graphql-tag';

import type { CrmOrder, MutationUpdateContactTrainingArgs } from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';

class GraphQLCrmRequester {
  public updateContactTrainings = async (userTrainings: UpdateContactTrainingInfoRequestDto): Promise<any> => {
    const input = {
      finishedVideos: userTrainings.finishedVideos,
      startedVideos: userTrainings.startedVideos,
    };

    return graphQLRequester.mutate<{ updateContactTrainings: UpdateContactTrainingInfoRequestDto }, MutationUpdateContactTrainingArgs>({
      mutation: gql`
        mutation Mutation($input: UpdateContactTrainingInfoInput!) {
          updateContactTraining(input: $input)
        }
      `,
      variables: {
        input: input,
      },
    });
  };

  public getOrders = (): Promise<CrmOrder[]> => {
    return graphQLRequester
      .query<{ orders: CrmOrder[] }>({
        query: gql`
          query Orders {
            orders {
              id
              createdAt
              deletedAt
              fleetId
              orderClass
              details
              tickets {
                ... on Scheduling {
                  id
                  pipeline
                  addressInformation {
                    zipCode
                    address
                    additionalInformation
                  }
                  clientInformation {
                    name
                    document
                    email
                    phone
                    secondaryPhone
                    responsibleForTrackingScheduling
                    installationDoneByClient
                    installationProblem
                    additionalInformation
                  }
                  coblerInformation {
                    responsible
                    schedulingResponsible
                    contactResponsible
                    technicianResponsible
                  }
                  contactInformation {
                    contactMethod
                    contactMethodForResponsibleForInstallation
                    bestContactTime
                    bestWayToContact
                  }
                  dateInformation {
                    scheduledDay
                    scheduledHour
                    installedDate
                    preferredDay1
                    preferredDay2
                    preferredDay3
                    preferredHour1
                    preferredHour2
                    preferredHour3
                  }
                  fieldControlInformation {
                    fieldObservations
                    technicalTeamObservation
                  }
                  vehicleInformation {
                    licensePlates
                    models
                  }
                  schedulingSubCategory
                  scheduledDate
                  scheduledTime
                  installedDate
                  associatedDealId
                  details
                  fleetId
                  status
                  deviceIds
                  deviceCobliIds
                  deviceTotalQuantity {
                    removable
                    installed
                    camera
                    immobilizer
                    identifiers
                    redeCan
                    total
                  }
                  type
                  createdAt
                  deletedAt
                }
                ... on Return {
                  id
                  pipeline
                  addressInformation {
                    zipCode
                    address
                    additionalInformation
                  }
                  clientInformation {
                    name
                    document
                    email
                    phone
                    secondaryPhone
                    responsibleForTrackingScheduling
                    installationDoneByClient
                    installationProblem
                    additionalInformation
                  }
                  coblerInformation {
                    responsible
                    schedulingResponsible
                    contactResponsible
                    technicianResponsible
                  }
                  contactInformation {
                    contactMethod
                    contactMethodForResponsibleForInstallation
                    bestContactTime
                    bestWayToContact
                  }
                  dateInformation {
                    scheduledDay
                    scheduledHour
                    installedDate
                    preferredDay1
                    preferredDay2
                    preferredDay3
                    preferredHour1
                    preferredHour2
                    preferredHour3
                  }
                  fieldControlInformation {
                    fieldObservations
                    technicalTeamObservation
                  }
                  vehicleInformation {
                    licensePlates
                    models
                  }
                  associatedDealId
                  details
                  fleetId
                  status
                  deviceIds
                  deviceCobliIds
                  deviceTotalQuantity {
                    removable
                    installed
                    camera
                    immobilizer
                    identifiers
                    redeCan
                    total
                  }
                  responsibleUser {
                    id
                    name
                  }
                  clientCNPJ
                  postingDate
                  arrivalOfDevicesAtCobli
                  scheduleDate
                  shippingCode
                  returnType
                  associatedDealId
                  details
                  fleetId
                  status
                  deviceIds
                  deviceCobliIds
                  type
                  createdAt
                  deletedAt
                  closedDate
                  deadline
                  penaltyPrice
                }
                ... on Shipping {
                  id
                  pipeline
                  contactName
                  shippingDate
                  trackingCode
                  shippingCompany
                  shippingCompanyLink
                  estimatedTimeOfArrival
                  devicesWithInstallation
                  devicesWithoutInstallation
                  addressInformation {
                    zipCode
                    address
                    additionalInformation
                  }
                  associatedDealId
                  details
                  fleetId
                  status
                  deviceIds
                  deviceCobliIds
                  deviceTotalQuantity {
                    removable
                    installed
                    camera
                    immobilizer
                    identifiers
                    redeCan
                    total
                  }
                  type
                  createdAt
                  deletedAt
                }
                associatedDealId
                createdAt
                deletedAt
                details
                deviceCobliIds
                deviceIds
                fleetId
                id
                status
                type
                deviceTotalQuantity {
                  removable
                  installed
                  camera
                  immobilizer
                  identifiers
                  redeCan
                  total
                }
              }
              pipeline
              deviceTotalQuantity {
                removable
                installed
                camera
                immobilizer
                identifiers
                redeCan
                total
              }
              associatedContact {
                id
                name
                email
                phone
              }
            }
          }
        `,
      })
      .then((response) => response.orders);
  };

  public getSchedulingHolidays = (): Promise<Array<Date>> => {
    return graphQLRequester
      .query<{ schedulingHolidays: Array<string> }>({
        query: gql`
          query Query {
            schedulingHolidays
          }
        `,
      })
      .then((graphQLResponse) =>
        graphQLResponse.schedulingHolidays.map((x) => {
          var dateArray = x.split('-');
          var year = dateArray[0];
          var month = Number.parseInt(dateArray[1], 10) - 1;
          var date = dateArray[2];
          // @ts-expect-error
          return new Date(year, month, date);
        })
      );
  };
}

export default GraphQLCrmRequester;
